import { ROW_TYPES } from './constants';
import { computeGroupCounts, computeMoveTypeCounts } from './statusCounts';
import { flattenTrailerSpottedFields } from './trailerNormalization';
import type { SwapBoardGroup, SwapBoardTrailer, TrailerMoveMasterRow } from './types';

const attachGroupContextToTrailer = (
  trailer: SwapBoardTrailer,
  group: SwapBoardGroup
): SwapBoardTrailer =>
  flattenTrailerSpottedFields({
    ...trailer,
    id: trailer.pickupId,
    shipperGroupId: group.rmaxShipperCode,
    groupId: trailer.groupId ?? group.rmaxShipperCode,
    rowType: ROW_TYPES.GROUPED,
    shipperName: group.shipperName,
    rmaxShipperCode: group.rmaxShipperCode,
    closeTime: trailer.closeTime ?? null,
  }) as SwapBoardTrailer;

export const buildTrailerMoveMasterRows = (
  apiGroups: SwapBoardGroup[] = []
): TrailerMoveMasterRow[] => {
  if (!Array.isArray(apiGroups)) {
    return [];
  }

  return apiGroups.map((group) => {
    const trailers = (group.trailers || []).map((trailer) =>
      attachGroupContextToTrailer(trailer, group)
    );
    const groupCounts = computeGroupCounts(trailers);
    const moveTypeCounts = computeMoveTypeCounts(trailers);

    return {
      id: group.rmaxShipperCode,
      groupId: group.rmaxShipperCode,
      rowType: ROW_TYPES.GROUP_HEADER,
      isMergedPickup: true,
      rmaxShipperCode: group.rmaxShipperCode,
      shipperName: group.shipperName,
      spottedTrailers: group.spottedTrailers ?? 0,
      groupCount: trailers.length,
      trailers,
      ...groupCounts,
      ...moveTypeCounts,
    };
  });
};

export const flattenMasterRowChildren = (masterRows: TrailerMoveMasterRow[] = []) =>
  masterRows.flatMap((masterRow) => masterRow.trailers ?? []);

export const isTrailerMoveMasterRow = (
  row?: SwapBoardTrailer | TrailerMoveMasterRow | null
): row is TrailerMoveMasterRow => Array.isArray(row?.trailers);

export const isMasterOrHeaderRow = (row?: SwapBoardTrailer | TrailerMoveMasterRow | null) =>
  isTrailerMoveMasterRow(row) || row?.rowType === ROW_TYPES.GROUP_HEADER;

export const transformSwapBoardResponse = (apiGroups: SwapBoardGroup[] = []) => {
  if (!Array.isArray(apiGroups)) {
    return [];
  }

  return apiGroups.flatMap((group) => {
    const shipperGroupId = group.rmaxShipperCode;
    const moveTypeCounts = computeMoveTypeCounts(group.trailers);
    const headerRow = {
      id: shipperGroupId,
      groupId: shipperGroupId,
      rowType: ROW_TYPES.GROUP_HEADER,
      isMergedPickup: true,
      shipperName: group.shipperName,
      rmaxShipperCode: group.rmaxShipperCode,
      spottedTrailers: group.spottedTrailers ?? 0,
      groupCount: group.trailers?.length ?? 0,
      ...moveTypeCounts,
    };

    const childRows = (group.trailers || []).map((trailer) =>
      flattenTrailerSpottedFields({
        ...trailer,
        id: trailer.pickupId,
        shipperGroupId,
        rowType: ROW_TYPES.GROUPED,
        shipperName: group.shipperName,
        rmaxShipperCode: group.rmaxShipperCode,
        closeTime: trailer.closeTime ?? null,
      })
    );

    return [headerRow, ...childRows];
  });
};
